'use client'

import { useMemo } from 'react'
import useSWR from 'swr'
import { getBrowserLanguage } from '~/features/i18n/utils/getBrowserLanguage'
import { isSupportedLanguage } from '~/features/i18n/utils/isSupportedLanguage'
import {
  getLanguageLocalStorage,
  setLanguageLocalStorage,
} from '~/features/i18n/utils/languageLocalStorage'
import { useAxios } from '~/libs/axios'
import { getLanguage } from '~/libs/language'
import { Language, Profile } from '~/types/api/users'
import { useLoginInfo } from './useLoginInfo'

export const useMyLanguage = () => {
  const axios = useAxios()
  const loginInfo = useLoginInfo()
  const { data: profile, isLoading } = useSWR<Profile>(
    loginInfo.status === 'signedIn' ? `/v2/user/edit` : null,
    (args: string) => axios.get(args).then((res) => res.data),
  )

  const language: Language = useMemo(() => {
    if (isLoading) return 'en'

    const myLanguage = profile !== undefined ? profile.language : null
    const browserLanguage = getBrowserLanguage()
    const localStorageLanguage = getLanguageLocalStorage()
    const language = getLanguage(typeof window !== 'undefined' ? window.location.hostname : null)
    const paramLanguage =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('lang')
        : null
    let selectedLanguage: Language = 'en'

    /**
     * 言語の設定優先順位
     * ① URLのサブドメインに紐づく言語
     * ② ユーザプロフィールに紐づく言語 (ユーザが設定した言語)
     * ③ URLのlangパラメータ
     * ④ 設定変更した際に保存したローカルストレージの値 (language)
     * ⑤ ブラウザのデフォルト言語
     * ⑥ 英語
     */
    if (language) {
      selectedLanguage = language
    } else if (myLanguage !== null) {
      selectedLanguage = myLanguage
    } else if (paramLanguage && isSupportedLanguage(paramLanguage)) {
      selectedLanguage = paramLanguage as Language
      const url = new URL(window.location.href)
      url.searchParams.delete('lang')
      window.history.replaceState(window.history.state, '', url)
    } else if (localStorageLanguage && isSupportedLanguage(localStorageLanguage)) {
      selectedLanguage = localStorageLanguage as Language
    } else if (isSupportedLanguage(browserLanguage)) {
      selectedLanguage = browserLanguage as Language
    }

    if (selectedLanguage !== localStorageLanguage) {
      setLanguageLocalStorage(selectedLanguage)
    }

    return selectedLanguage
  }, [isLoading, profile])

  return language
}
